package com.tregz.tremenz.io.model

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.decodeFromJsonElement
import kotlinx.serialization.decodeFromString

private val json = Json {
    ignoreUnknownKeys = true
    isLenient = true
    coerceInputValues = true
}

private const val MILLIS_PER_DAY = 86_400_000L

/**
 * Called by the parsers with how many entries were dropped out of how many were present, so the
 * loaders can log it and treat "everything dropped" as a failed load rather than an empty catalogue.
 */
typealias ModelSkipped = (skipped: Int, total: Int) -> Unit

private val ignoreSkipped: ModelSkipped = { _, _ -> }

/** The payload root as an object; a captive-portal page or a bare array is an error the loaders report. */
private fun parseRoot(jsonString: String): JsonObject =
    json.parseToJsonElement(jsonString) as? JsonObject
        ?: throw IllegalArgumentException("The payload root is not a JSON object")

/**
 * Decodes the `miksing-export.json` payload into songs, sorted by artist. An entry that is not a
 * well-formed object is skipped and reported through [onSkipped], as the per-platform parsers this
 * replaces did; a payload that is not a JSON object throws, and the loaders catch it.
 */
fun parseSongsJson(jsonString: String, onSkipped: ModelSkipped = ignoreSkipped): List<ModelData.Song> {
    val songs = parseRoot(jsonString)["song"] as? JsonObject ?: return emptyList()
    val parsed = songs.mapNotNull { (id, element) ->
        element.decodeOrNull<ModelWireSong>()?.toSong(id)
    }.sortedBy { it.artist.lowercase() }
    onSkipped(songs.size - parsed.size, songs.size)
    return parsed
}

/**
 * Decodes the `music_videos.json` payload into the channel programming: channel name, then the a-z
 * hour bucket [ModelChannel.bucketsAt] resolves, then the songs that bucket plays.
 */
fun parseMusicVideosJson(
    jsonString: String,
    onSkipped: ModelSkipped = ignoreSkipped
): Map<String, Map<String, List<ModelData.Song>>> {
    val root = parseRoot(jsonString)
    val channels = mutableMapOf<String, Map<String, List<ModelData.Song>>>()
    var total = 0
    var skipped = 0
    for ((channel, bucketElement) in root) {
        val bucketObject = bucketElement as? JsonObject ?: continue
        val buckets = mutableMapOf<String, List<ModelData.Song>>()
        for ((bucket, array) in bucketObject) {
            if (array !is JsonArray) continue
            val songs = array.mapNotNull { it.decodeOrNull<ModelWireClip>()?.toSong() }
            total += array.size
            skipped += array.size - songs.size
            buckets[bucket] = songs
        }
        channels[channel] = buckets
    }
    onSkipped(skipped, total)
    return channels
}

/**
 * Decodes the `mixes.json` payload into audio/video pairs for the Playlists catalogue.
 */
fun parseMixesJson(
    jsonString: String,
    onSkipped: ModelSkipped = ignoreSkipped
): List<Pair<ModelData.Audio?, ModelData.Video>> {
    val root = parseRoot(jsonString)
    val pairs = mutableListOf<Pair<ModelData.Audio?, ModelData.Video>>()
    var total = 0
    for ((category, array) in root) {
        if (array !is JsonArray) continue
        for (element in array) {
            total++
            val mix = element.decodeOrNull<ModelWireMix>() ?: continue
            if (mix.id.isEmpty()) continue
            pairs.add(mix.toPair(category))
        }
    }
    onSkipped(total - pairs.size, total)
    return pairs
}

/**
 * Maps a Firestore mix document, already serialized to JSON, onto the same audio/video pair
 * [parseMixesJson] builds. The document carries no `id` or `path`: they are the document id and the
 * collection it was read from. [trackJsons] are the `list` subcollection documents, each with its id.
 * Null when the document itself is malformed, so one bad document does not fail its collection.
 */
fun parseMixDocument(
    id: String,
    path: String,
    mixJson: String,
    trackJsons: List<String>
): Pair<ModelData.Audio?, ModelData.Video>? {
    val mix = runCatching { json.decodeFromString<ModelWireMix>(mixJson) }.getOrNull() ?: return null
    val tracks = trackJsons.mapNotNull { runCatching { json.decodeFromString<ModelWireTrack>(it) }.getOrNull() }
    return mix.copy(id = id, path = path, list = tracks).toPair(path)
}

private inline fun <reified T> JsonElement.decodeOrNull(): T? =
    if (this is JsonObject) runCatching { json.decodeFromJsonElement<T>(this) }.getOrNull() else null

private fun ModelWireSong.toSong(id: String): ModelData.Song {
    val released = releasedAt?.toLong()
    return ModelData.Song(
        id = id,
        artist = artist,
        featuring = featuring,
        mixedBy = mixedBy,
        name = name,
        genre = genre ?: (mixedBy?.let { "Mix: $it" } ?: "YouTube"),
        year = if (released != null && released > 0) utcYear(released) else 0L,
        releasedAt = released
    )
}

/** Null when the clip has no resolvable video id: it is unplayable, so it never reaches the queue. */
private fun ModelWireClip.toSong(): ModelData.Song? {
    val id = youtubeVideoId(youtubeUrl) ?: return null
    return ModelData.Song(
        id = id,
        artist = artist,
        name = title,
        genre = genre?.ifBlank { null } ?: "YouTube",
        year = releaseYear(releaseDate),
        artwork = artwork?.takeIf { it.isNotBlank() }
    )
}

private fun ModelWireMix.toPair(category: String): Pair<ModelData.Audio?, ModelData.Video> {
    val mixPath = path ?: category
    val mixYear = year.toLong()
    val tracks = list.map { wire ->
        ModelData.Track(
            id = wire.id,
            artist = wire.artist,
            cut = wire.cut,
            edition = wire.edition,
            featuring = wire.featuring,
            mix = wire.mix,
            title = wire.title
        )
    }
    val audio = sono
        ?.takeIf { it.contains("mixcloud") || it.contains("soundcloud") }
        ?.let { ModelData.Audio(id, name, mixPath, it, type, mixYear, tracks, posterUrl) }
    val video = ModelData.Video(id, host, name, mixPath, type, mixYear, tracks, posterUrl)
    return audio to video
}

/** The calendar year, in UTC, of an epoch-millisecond timestamp (civil-from-days, no platform API). */
internal fun utcYear(epochMillis: Long): Long {
    val z = epochMillis.floorDiv(MILLIS_PER_DAY) + 719_468
    val era = z.floorDiv(146_097L)
    val dayOfEra = z - era * 146_097
    val yearOfEra = (dayOfEra - dayOfEra / 1_460 + dayOfEra / 36_524 - dayOfEra / 146_096) / 365
    val dayOfYear = dayOfEra - (365 * yearOfEra + yearOfEra / 4 - yearOfEra / 100)
    val monthIndex = (5 * dayOfYear + 2) / 153
    val month = if (monthIndex < 10) monthIndex + 3 else monthIndex - 9
    val year = yearOfEra + era * 400
    return if (month <= 2) year + 1 else year
}
